import { Component, OnDestroy, OnInit } from '@angular/core';
import { LocalizationService } from 'src/app/service/localization.service';
import RemoteSpeechRecognition from 'src/app/core/speech-recognition/remote-speech-recognition';
import {
  AudioResponse,
  AudioResponseKey,
  VoiceCommandResponse,
  VoiceCommandResponseKey,
} from 'src/app/data/enum/audio-response';
import {
  DialogFlowResult,
  ListeningStatus,
  ListeningStatusKey,
  SpeechRecognitionResponse,
} from 'src/app/data/type/audio';
import { JsonKey } from 'src/app/data/type/locale-copy-key';
import { getAudioResponseCopy, getSpeechButtonCopy } from 'src/app/utils/localization';
import { environment } from 'src/environments/environment';
import { VoiceSearchService } from 'src/app/service/voice-search.service';
import { ParameterService } from 'src/app/service/parameter.service';
import { Parameter } from 'src/app/data/type/parameter';
import { isGenericAssistantButton } from 'src/app/utils/version';

@Component({
  selector: 'app-speech-button',
  templateUrl: './speech-button.component.html',
  styleUrls: ['./speech-button.component.scss'],
})
export class SpeechButtonComponent implements OnInit, OnDestroy {
  private speech: RemoteSpeechRecognition;
  private audioResponseCopy = {
    [JsonKey.AUDIO_RESPONSE_ERROR]: '',
    [JsonKey.AUDIO_RESPONSE_LIGHTS]: '',
    [JsonKey.AUDIO_RESPONSE_FRONT_DOOR]: '',
  };
  public copy = {
    [JsonKey.SPEECH_BUTTON_ARIA_LABEL]: '',
  };

  public jsonKey = JsonKey;
  public transcript = '';
  public isDisabled = false;
  public baseHref = environment.baseHref;
  private listeningStatus: ListeningStatusKey;
  private audio = new Audio();
  private isGenericAssistantButton = false;
  private listeningTimeoutId = null;

  constructor(
    private voiceSearchService: VoiceSearchService,
    private localizationService: LocalizationService,
    private parameterService: ParameterService,
  ) {
    this.audio.autoplay = true;

    this.speech = new RemoteSpeechRecognition({
      socketUrl: environment.production
        ? 'wss://api-dot-gweb-interactivedemo-boreal.uc.r.appspot.com/ws'
        : 'wss://api-dot-ggl-retail-l1-demo-staging.uc.r.appspot.com/ws',

      onStatusChanged: this.handleStatusChange,
      onSpeechResultsChanged: this.updateSpeechResults,
      onDialogFlowResultReceived: this.handleDialogflowResult,
    });
  }

  private deviceId = null;

  ngOnInit(): void {
    const brandName = this.parameterService.getParam(Parameter['brand']);
    this.audioResponseCopy = getAudioResponseCopy(this.localizationService);
    this.copy = getSpeechButtonCopy(this.localizationService);
    this.isGenericAssistantButton = isGenericAssistantButton(brandName);

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            this.deviceId = devices.find((device) => device.kind === 'audioinput').deviceId;
          })
          .catch((error) => console.log(`Cannot setup default device: ${error}`));
      })
      .catch(() => {
        this.voiceSearchService.triggerStatusEvent(ListeningStatus.ERROR);
      });
  }

  ngOnDestroy(): void {
    this.audio.removeEventListener('ended', this.handleAudioEnded);
    this.speech.onDestroy();
    this.stopRecognition();
  }

  handleAudioEnded = () => {
    this.isDisabled = false;
    this.voiceSearchService.triggerAudioEndedEvent();
  };

  handleStatusChange = (listeningStatus: ListeningStatusKey) => {
    this.listeningStatus = listeningStatus;
    this.voiceSearchService.triggerStatusEvent(listeningStatus);
  };

  private handleDialogflowResult = (data: DialogFlowResult) => {
    // data.text for voice response is either 'error', 'lights', or 'front-door'.
    // This corresponds to each mp3 file's name.
    const responseKey = data.text as VoiceCommandResponseKey;

    if (VoiceCommandResponse[responseKey]) {
      let displayableText = '';

      switch (responseKey) {
        case AudioResponse.lights:
          displayableText = this.audioResponseCopy.AUDIO_RESPONSE_LIGHTS;
          break;
        case AudioResponse['front-door']:
          displayableText = this.audioResponseCopy.AUDIO_RESPONSE_FRONT_DOOR;
          break;
        default:
          displayableText = this.audioResponseCopy.AUDIO_RESPONSE_ERROR;
          break;
      }

      if (AudioResponse[responseKey]) {
        this.audio.addEventListener('ended', this.handleAudioEnded);

        this.audio.src = this.localizationService.getLocalizedAudio(
          responseKey as AudioResponseKey,
        );
      } else this.isDisabled = false;

      this.voiceSearchService.triggerSpeechResultsEvent(displayableText);
      this.voiceSearchService.triggerDialogFlowEvent(data);
    }
  };

  private setListeningTimout = () => {
    this.listeningTimeoutId = setTimeout(() => {
      if (!this.transcript) {
        this.transcript = 'time out';
        this.stopRecognition();
      }
    }, 5000);
  };

  updateSpeechResults = ({ alternatives, errorMessage, isFinal }: SpeechRecognitionResponse) => {
    errorMessage && console.log('Error getting transcriptions: ', errorMessage);
    if (this.listeningTimeoutId) {
      clearTimeout(this.listeningTimeoutId);
      this.listeningTimeoutId = null;
    }

    this.transcript = !!alternatives ? alternatives[0].transcript : 'Please try again';

    this.voiceSearchService.triggerSpeechResultsEvent(this.transcript);
    if (isFinal) {
      this.stopRecognition();
    }
  };

  public stopRecognition = () => {
    if (this.listeningStatus === ListeningStatus.LISTENING) this.speech.stop(this.transcript);
  };

  private isValidKey = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent;
    return !keyboardEvent.repeat && keyboardEvent.key === 'Enter';
  };

  public handleClick = (event: Event, fromKeyboard = false) => {
    this.transcript = '';

    event.stopPropagation();
    if (fromKeyboard && !this.isValidKey(event)) return;

    this.isDisabled = true;

    // Remove event listener to prevent the previous action from repeating if the user wants to try another option.
    this.audio.removeEventListener('ended', this.handleAudioEnded);
    this.audio.src =
      'data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

    if (this.listeningTimeoutId) {
      clearTimeout(this.listeningTimeoutId);
      this.listeningTimeoutId = null;
    }

    if (this.listeningStatus !== ListeningStatus.LISTENING) {
      this.speech.start(this.deviceId, this.setListeningTimout);
    }
  };

  public buttonStyles = {
    'background-image': `url(${this.baseHref}/assets/images/${
      this.isGenericAssistantButton
        ? 'abstract-remote/abstract-remote-assistant.png'
        : 'google-remote/google-remote-assistant.png'
    }`,
    'background-color': this.isGenericAssistantButton ? 'none' : '#1a73e8',
    'background-size': this.isGenericAssistantButton ? 'iniitial' : '60%',
  };
}
